import { useRef, useState } from "react";
import { track } from "@/lib/track";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Camera, Check, Copy, Download } from "lucide-react";

/**
 * FreeCard — public, no-auth wizard. Three quiet steps:
 *   1. Photo  →  2. Name  →  3. (Optional) phrase + email  →  4. Result
 *
 * Conversion philosophy:
 * - Zero friction: no signup, no email required, just a photo + name.
 * - The email field is optional and explicit ("Se vuoi ricevere il
 *   ricordo per email — facoltativo"). Capturing it secondarily.
 * - The end screen is the gentle hook: shows the card the visitor
 *   just built, then a single soft CTA towards the full Other Side.
 *
 * We bypass the shared `api` client (which auto-injects Bearer tokens)
 * because this endpoint is intentionally anonymous. Using a raw axios
 * call with no Authorization header avoids 401s for logged-out visitors.
 */
const PUBLIC_API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function FreeCard() {
    const nav = useNavigate();
    const [step, setStep] = useState(1);
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState("");
    const [name, setName] = useState("");
    const [phrase, setPhrase] = useState("");
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [result, setResult] = useState(null);
    const [copied, setCopied] = useState(false);
    const fileRef = useRef(null);

    const onPickPhoto = (file) => {
        setErr("");
        if (!file) return;
        if (file.size > 6 * 1024 * 1024) {
            setErr("La foto è troppo grande (max 6MB).");
            return;
        }
        if (!file.type.startsWith("image/")) {
            setErr("Solo immagini, per favore.");
            return;
        }
        setPhotoFile(file);
        const reader = new FileReader();
        reader.onload = (e) => setPhotoPreview(e.target.result);
        reader.readAsDataURL(file);
    };

    const fileToBase64 = (file) =>
        new Promise((resolve, reject) => {
            const r = new FileReader();
            r.onload = (e) => {
                const dataUrl = e.target.result;
                resolve(dataUrl.split(",", 2)[1]); // strip data:...;base64,
            };
            r.onerror = reject;
            r.readAsDataURL(file);
        });

    const submit = async () => {
        setErr("");
        setBusy(true);
        try {
            const photo_base64 = await fileToBase64(photoFile);
            const { data } = await axios.post(
                `${PUBLIC_API}/api/free-card`,
                {
                    name: name.trim(),
                    phrase: phrase.trim() || null,
                    email: email.trim() || null,
                    photo_base64,
                    photo_mime: photoFile.type || "image/jpeg",
                },
                { headers: { "Content-Type": "application/json" } },
            );
            setResult(data);
            setStep(4);
            // Track conversion — end of free-card funnel.
            track("free_card_created");
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (Array.isArray(detail) && detail[0]?.msg) {
                setErr(detail[0].msg.replace(/^Value error,\s*/, ""));
            } else if (typeof detail === "string") {
                setErr(detail);
            } else {
                setErr("Qualcosa è andato storto. Riprova fra un momento.");
            }
        } finally {
            setBusy(false);
        }
    };

    const downloadCard = async () => {
        try {
            const r = await fetch(result.image_url, { credentials: "omit" });
            const blob = await r.blob();
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `ricordo-${(result.presence_name || "presenza").toLowerCase().replace(/\s+/g, "-")}.png`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(a.href), 1000);
        } catch {
            window.open(result.image_url, "_blank", "noopener,noreferrer");
        }
    };

    const copyShareUrl = async () => {
        try {
            await navigator.clipboard.writeText(result.share_url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch { /* noop */ }
    };

    return (
        <div className="min-h-screen bg-ink-paper text-ink-textHigh" data-testid="free-card-page">
            {/* Quiet header (no nav) — full focus on the form */}
            <header className="px-6 pt-10 pb-2 text-center">
                <div className="text-xs tracking-[0.22em] text-ink-gold uppercase">Other &nbsp; Side</div>
                <p className="mt-2 text-[11px] tracking-widest uppercase text-ink-textLow">
                    Crea un ricordo
                </p>
            </header>

            <main className="max-w-lg mx-auto px-6 py-10">
                {step === 1 && (
                    <Step
                        eyebrow="Passo 1 di 3"
                        title="Una foto da custodire"
                        body="Una sola immagine, quella che senti più sua."
                        testId="free-card-step-1"
                    >
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => onPickPhoto(e.target.files?.[0])}
                            data-testid="free-card-photo-input"
                        />
                        {!photoPreview ? (
                            <button
                                type="button"
                                onClick={() => fileRef.current?.click()}
                                className="w-full aspect-square border-2 border-dashed border-ink-gold/30 rounded-sm flex flex-col items-center justify-center gap-3 text-ink-textLow hover:border-ink-gold/60 hover:text-ink-textMid transition-colors"
                                data-testid="free-card-photo-picker"
                            >
                                <Camera size={32} className="opacity-60" />
                                <span className="text-sm font-serif italic">Scegli una foto</span>
                                <span className="text-xs opacity-60">JPG / PNG · max 6MB</span>
                            </button>
                        ) : (
                            <div className="space-y-4">
                                <img
                                    src={photoPreview}
                                    alt="Anteprima"
                                    className="w-full aspect-square object-cover rounded-sm border border-ink-gold/30"
                                />
                                <button
                                    type="button"
                                    onClick={() => fileRef.current?.click()}
                                    className="text-xs text-ink-textLow underline"
                                >
                                    Cambia foto
                                </button>
                            </div>
                        )}
                        {err && <p className="mt-3 text-sm text-ink-danger" data-testid="free-card-error">{err}</p>}
                        <button
                            disabled={!photoFile}
                            onClick={() => { setErr(""); setStep(2); }}
                            className="btn-primary w-full mt-8 disabled:opacity-50"
                            data-testid="free-card-next-1"
                        >
                            Continua
                        </button>
                    </Step>
                )}

                {step === 2 && (
                    <Step
                        eyebrow="Passo 2 di 3"
                        title="Come si chiama?"
                        body="Il nome con cui vuoi ricordarla."
                        testId="free-card-step-2"
                    >
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            maxLength={60}
                            placeholder="Es. Nonna Maria"
                            className="w-full bg-transparent border-b border-ink-gold/30 py-3 text-lg font-serif text-ink-textHigh placeholder:text-ink-textLow focus:outline-none focus:border-ink-gold transition-colors"
                            data-testid="free-card-name-input"
                        />
                        {err && <p className="mt-3 text-sm text-ink-danger">{err}</p>}
                        <div className="grid grid-cols-2 gap-3 mt-8">
                            <button onClick={() => setStep(1)} className="btn-ghost">Indietro</button>
                            <button
                                disabled={!name.trim()}
                                onClick={() => { setErr(""); setStep(3); }}
                                className="btn-primary disabled:opacity-50"
                                data-testid="free-card-next-2"
                            >
                                Continua
                            </button>
                        </div>
                    </Step>
                )}

                {step === 3 && (
                    <Step
                        eyebrow="Passo 3 di 3"
                        title="Una frase, se vuoi."
                        body="Qualcosa che diceva. Una cosa che ti rimane addosso. Lascia vuoto se preferisci."
                        testId="free-card-step-3"
                    >
                        <input
                            type="text"
                            value={phrase}
                            onChange={(e) => setPhrase(e.target.value)}
                            maxLength={80}
                            placeholder="Es. Mangia, che si fredda."
                            className="w-full bg-transparent border-b border-ink-gold/30 py-3 text-base font-serif italic text-ink-textHigh placeholder:text-ink-textLow focus:outline-none focus:border-ink-gold transition-colors"
                            data-testid="free-card-phrase-input"
                        />
                        <div className="mt-8">
                            <p className="text-xs tracking-widest uppercase text-ink-textLow mb-3">
                                Email · facoltativo
                            </p>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                maxLength={200}
                                placeholder="Per ricevere il ricordo via email"
                                className="w-full bg-transparent border-b border-ink-gold/20 py-2 text-sm font-serif text-ink-textHigh placeholder:text-ink-textLow focus:outline-none focus:border-ink-gold transition-colors"
                                data-testid="free-card-email-input"
                            />
                            <p className="text-[11px] text-ink-textLow font-serif italic mt-2 leading-relaxed">
                                Non userò la tua email per altro. Solo per mandarti questo ricordo, se vuoi.
                            </p>
                        </div>
                        {err && <p className="mt-3 text-sm text-ink-danger">{err}</p>}
                        <div className="grid grid-cols-2 gap-3 mt-8">
                            <button onClick={() => setStep(2)} className="btn-ghost" disabled={busy}>Indietro</button>
                            <button
                                disabled={busy}
                                onClick={submit}
                                className="btn-primary disabled:opacity-50"
                                data-testid="free-card-submit"
                            >
                                {busy ? "Sto creando…" : "Crea il ricordo"}
                            </button>
                        </div>
                    </Step>
                )}

                {step === 4 && result && (
                    <div className="animate-fade-in" data-testid="free-card-result">
                        <p className="label-eyebrow text-ink-gold text-center">Il tuo ricordo</p>
                        <h1 className="font-serif text-3xl text-center text-ink-textHigh mt-3 mb-2">
                            È pronto.
                        </h1>
                        <p className="text-center text-sm text-ink-textLow font-serif italic leading-relaxed mb-8">
                            È tuo. Da custodire o condividere coi tuoi cari, se ne avrai voglia.
                        </p>
                        <img
                            src={result.image_url}
                            alt={`Ricordo di ${result.presence_name}`}
                            className="w-full aspect-square object-cover rounded-sm border border-ink-gold/20 mb-6"
                            data-testid="free-card-image"
                        />
                        <div className="grid grid-cols-2 gap-3 mb-8">
                            <button onClick={downloadCard} className="btn-secondary inline-flex items-center justify-center gap-2 text-sm" data-testid="free-card-download">
                                <Download size={15} /> Scarica
                            </button>
                            <button onClick={copyShareUrl} className="btn-secondary inline-flex items-center justify-center gap-2 text-sm" data-testid="free-card-copy">
                                {copied ? <><Check size={15} /> Copiato</> : <><Copy size={15} /> Copia link</>}
                            </button>
                        </div>
                        {/* Soft conversion block — explanatory, not pushy */}
                        <div className="border-t border-ink-gold/20 pt-8 text-center">
                            <p className="font-serif text-xl text-ink-textHigh leading-relaxed">
                                Su Other Side puoi fare anche di più.
                            </p>
                            <p className="font-serif italic text-sm text-ink-textLow leading-relaxed mt-3 mb-7">
                                Parlare con la persona cara quando ti manca,<br/>
                                conservarne i vocali, le foto, le ricorrenze.<br/>
                                Una presenza che resta — non un sostituto.
                            </p>
                            <button
                                onClick={() => nav("/")}
                                className="btn-primary"
                                data-testid="free-card-conversion-cta"
                            >
                                Conosci Other Side
                            </button>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}

function Step({ eyebrow, title, body, children, testId }) {
    return (
        <div className="animate-fade-in" data-testid={testId}>
            <p className="label-eyebrow text-ink-gold text-center">{eyebrow}</p>
            <h1 className="font-serif text-3xl text-center text-ink-textHigh mt-3 mb-3">{title}</h1>
            <p className="text-center text-sm text-ink-textLow font-serif italic leading-relaxed mb-10">
                {body}
            </p>
            {children}
        </div>
    );
}
